// proceduresStore — client cache of CIFP data fetched from
// /api/procedures.
//
// Two caches:
//   - manifest: cycle currency / counts (small, fetched once per session)
//   - byAirport: { ICAO → Procedure[] } populated lazily as users open
//     airports. Cached for 30 minutes; refreshed on demand.
//
// The store doesn't mutate the flight plan — that lives in planStore.
// We only own "what procedures are available" + "which transition has
// the user picked". Insertion-into-plan logic lives in
// src/lib/procedureExpansion.ts.

import { create } from 'zustand';
import type { Procedure, RouteType } from '../lib/cifp/cifp';
import { kvGet, kvSet } from '../lib/idb';

// IDB keys for offline persistence (OAC6).
const IDB_MANIFEST_KEY = 'fyrefly:procedures:manifest:v1';
const IDB_AIRPORTS_KEY  = 'fyrefly:procedures:airports:v1';
// Keep at most this many airports in the IDB snapshot (most-recently-used).
const IDB_MAX_AIRPORTS  = 20;

interface CifpManifest {
  cycle: string;
  effective: string;
  expires: string;
  importedAt?: number;
  counts?: Record<string, number>;
}

interface CachedAirport {
  fetchedAt: number;
  procedures: Procedure[];
}

interface ProceduresState {
  manifest: CifpManifest | null;
  manifestLoading: boolean;
  byAirport: Map<string, CachedAirport>;
  loadingAirports: Set<string>;
  errors: Map<string, string>;

  /** Fetch manifest if we don't have it yet. */
  ensureManifest: () => Promise<CifpManifest | null>;
  /** Fetch procedures for one airport (cached for 30min). */
  loadAirport: (icao: string) => Promise<Procedure[]>;
  /** Force a fresh fetch ignoring cache. */
  refreshAirport: (icao: string) => Promise<Procedure[]>;
  /** Direct accessor; returns null when not yet fetched. */
  getAirport: (icao: string) => Procedure[] | null;
}

const AIRPORT_CACHE_MS = 30 * 60 * 1000;

export const useProceduresStore = create<ProceduresState>((set, get) => {
  // Rehydrate manifest + recent airports from IDB at store creation (OAC6).
  Promise.all([
    kvGet<CifpManifest>(IDB_MANIFEST_KEY),
    kvGet<Array<[string, CachedAirport]>>(IDB_AIRPORTS_KEY),
  ]).then(([storedManifest, storedAirports]) => {
    const { manifest, byAirport } = get();
    if (storedManifest && !manifest) {
      set({ manifest: storedManifest });
    }
    if (Array.isArray(storedAirports) && byAirport.size === 0) {
      set({ byAirport: new Map(storedAirports) });
    }
  }).catch(() => { /* IDB read failure is non-fatal */ });

  return {
    manifest: null,
    manifestLoading: false,
    byAirport: new Map(),
    loadingAirports: new Set(),
    errors: new Map(),

  ensureManifest: async () => {
    const cur = get().manifest;
    if (cur) return cur;
    if (get().manifestLoading) return null;
    set({ manifestLoading: true });
    const manifestController = new AbortController();
    const manifestTimer = setTimeout(() => manifestController.abort(), 8000);
    try {
      // Remove cache: 'no-store' so the SW can serve cached data offline.
      // 8 s timeout so a stalled upstream can't park the loader (HI-4).
      const r = await fetch('/api/procedures', { signal: manifestController.signal });
      clearTimeout(manifestTimer);
      if (!r.ok) {
        set({ manifestLoading: false });
        return null;
      }
      const m = (await r.json()) as CifpManifest;
      // Persist manifest to IDB for tab-eviction recovery.
      kvSet(IDB_MANIFEST_KEY, m).catch(() => { /* non-fatal */ });
      set({ manifest: m, manifestLoading: false });
      return m;
    } catch (e) {
      clearTimeout(manifestTimer);
      console.warn('[procedures] manifest fetch failed:', e);
      set({ manifestLoading: false });
      return null;
    }
  },

  loadAirport: async (icao) => {
    const norm = icao.trim().toUpperCase();
    if (!norm) return [];
    const cached = get().byAirport.get(norm);
    if (cached && Date.now() - cached.fetchedAt < AIRPORT_CACHE_MS) {
      return cached.procedures;
    }
    return get().refreshAirport(norm);
  },

  refreshAirport: async (icao) => {
    const norm = icao.trim().toUpperCase();
    if (!norm) return [];
    if (get().loadingAirports.has(norm)) {
      // Already in flight — wait for it.
      return new Promise<Procedure[]>((resolve) => {
        const t = setInterval(() => {
          const c = get().byAirport.get(norm);
          if (c) { clearInterval(t); resolve(c.procedures); }
          else if (!get().loadingAirports.has(norm)) { clearInterval(t); resolve([]); }
        }, 100);
      });
    }

    set((s) => ({ loadingAirports: new Set(s.loadingAirports).add(norm) }));
    // HI-4 audit 2026-05-22: wall-clock timeout + offline fallback.
    // Previously `cache: 'no-store'` prevented the SW from serving
    // cached data offline, AND there was no abort — a hung upstream
    // on bad LTE would spin the loader forever and the user would
    // see no approaches/SIDs/STARs for the airport. Now:
    //   - drop no-store so the SW's networkFirst can serve cached
    //   - 8 s AbortController
    //   - on any failure (HTTP error, timeout, network), keep the
    //     existing cached procedures in place (even if >30 min old)
    //     so the pilot still sees something they can fly.
    const controller = new AbortController();
    const abortTimer = setTimeout(() => controller.abort(), 8000);
    try {
      const r = await fetch(`/api/procedures?airport=${encodeURIComponent(norm)}`, {
        signal: controller.signal,
      });
      clearTimeout(abortTimer);
      if (!r.ok) {
        set((s) => {
          const next = new Set(s.loadingAirports); next.delete(norm);
          const errs = new Map(s.errors); errs.set(norm, `HTTP ${r.status}`);
          return { loadingAirports: next, errors: errs };
        });
        const stale = get().byAirport.get(norm);
        return stale?.procedures ?? [];
      }
      const body = await r.json() as { airport: string; procedures: Procedure[] };
      const procedures = Array.isArray(body.procedures) ? body.procedures : [];
      set((s) => {
        const next = new Map(s.byAirport);
        next.set(norm, { fetchedAt: Date.now(), procedures });
        const loading = new Set(s.loadingAirports); loading.delete(norm);
        const errs = new Map(s.errors); errs.delete(norm);
        // Persist most-recently-used airports to IDB for tab-eviction recovery.
        // Limit snapshot size to IDB_MAX_AIRPORTS most-recent entries.
        const entries = [...next.entries()];
        const recent = entries.slice(-IDB_MAX_AIRPORTS);
        kvSet(IDB_AIRPORTS_KEY, recent).catch(() => { /* non-fatal */ });
        return { byAirport: next, loadingAirports: loading, errors: errs };
      });
      return procedures;
    } catch (e: unknown) {
      clearTimeout(abortTimer);
      const msg = e instanceof Error ? e.message : String(e);
      set((s) => {
        const next = new Set(s.loadingAirports); next.delete(norm);
        const errs = new Map(s.errors); errs.set(norm, msg);
        return { loadingAirports: next, errors: errs };
      });
      // Offline / upstream timeout: surface whatever stale cache we
      // have instead of returning an empty list (which made plates &
      // procedures simply disappear from the UI).
      const stale = get().byAirport.get(norm);
      return stale?.procedures ?? [];
    }
  },

  getAirport: (icao) => {
    const c = get().byAirport.get(icao.trim().toUpperCase());
    return c ? c.procedures : null;
  },
  };
});

// ── Helpers ──────────────────────────────────────────────────────────────

export function filterByRouteType(procs: Procedure[], type: RouteType): Procedure[] {
  return procs.filter((p) => p.routeType === type);
}
